import type { QueryClient } from "@tanstack/react-query";
import { installDemoFetchGuard } from "@/api/demoTransport";
import { DEMO_IDS, demoOrganizations } from "@/lib/demoWorkspace";
import { useAuthStore, type User } from "@/stores/auth";
import { useDemoWorkspaceStore } from "@/stores/demoWorkspace";
import { usePreferencesStore } from "@/stores/preferences";
import { useThemeStore } from "@/stores/theme";
import { useVisibilityStore } from "@/stores/visibility";
import { clearNotificationWorkspace } from "@/stores/notifications";

type Auth = ReturnType<typeof useAuthStore.getState>;
const storageKeys = ["active_org_id", "trace_preferences", "trace-theme", "theme", "trace-hv", "fontSize", "trace-notification-sound"] as const;
let queryClient: QueryClient | undefined;
let changing = false;
const projectedUsers = new WeakSet<User>();
let snapshot: {
  ownerId: string;
  user: User;
  organizations: Auth["organizations"];
  activeOrganizationId: string | null;
  preferences: Pick<ReturnType<typeof usePreferencesStore.getState>, "hideProduction" | "productionInfoSeen" | "showOrgBanner" | "layoutVariants" | "festivalTabs">;
  theme: ReturnType<typeof useThemeStore.getState>["theme"];
  visibility: ReturnType<typeof useVisibilityStore.getState>["visibility"];
  fontSize: string;
  storage: Map<string, string | null>;
} | null = null;
const snapshotKey = "trace_demo_workspace_original";
function saveSnapshot() {
  try {
    if (snapshot) sessionStorage.setItem(snapshotKey, JSON.stringify({ ...snapshot, storage: [...snapshot.storage] }));
  } catch { /* The in-memory snapshot still restores this tab when storage is unavailable. */ }
}

function clearQueryBoundary() {
  clearNotificationWorkspace();
  // Cancellation aborts in-flight reads before the next mode is rendered.
  void queryClient?.cancelQueries();
  queryClient?.clear();
}

/** Called once by App; both cache directions use the same boundary. */
export function bindDemoQueryClient(client: QueryClient) { queryClient = client; }

function projectDemoUser() {
  const auth = useAuthStore.getState();
  const demo = useDemoWorkspaceStore.getState();
  if (changing || !demo.active || !auth.user) return;
  if (demo.ownerId !== auth.user.id) {
    useDemoWorkspaceStore.getState().exit();
    return;
  }
  if (!snapshot) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(snapshotKey) ?? "null");
      if (saved?.ownerId === auth.user.id && saved.user?.id === auth.user.id && Array.isArray(saved.storage) && saved.preferences && saved.organizations) snapshot = { ...saved, user: auth.user, storage: new Map(saved.storage) };
    } catch { /* A malformed tab snapshot starts a fresh demo boundary. */ }
  }
  if (!snapshot) {
    const preferences = usePreferencesStore.getState();
    snapshot = {
      ownerId: auth.user.id, user: auth.user,
      organizations: auth.organizations, activeOrganizationId: auth.activeOrganizationId,
      preferences: { hideProduction: preferences.hideProduction, productionInfoSeen: preferences.productionInfoSeen, showOrgBanner: preferences.showOrgBanner, layoutVariants: preferences.layoutVariants, festivalTabs: preferences.festivalTabs },
      theme: useThemeStore.getState().theme, visibility: useVisibilityStore.getState().visibility,
      fontSize: document.documentElement.style.fontSize,
      storage: new Map(storageKeys.map((key) => [key, localStorage.getItem(key)])),
    };
    usePreferencesStore.setState({ hideProduction: false, showOrgBanner: false });
  } else if (!projectedUsers.has(auth.user)) snapshot.user = auth.user;
  if (auth.organizations.length && !auth.organizations.some((org) => org.id === DEMO_IDS.organization)) snapshot.organizations = auth.organizations;
  const user = { ...auth.user, effective_tier: "scale", subscription_status: "scale", ai_enabled: true, ai_monthly_limit: 1000, has_artist_profile: true, is_artist_only_producer: false };
  // The same signed-in ID and real admin flags bind tour writes and role gates.
  projectedUsers.add(user);
  saveSnapshot();
  changing = true;
  useAuthStore.setState({ user, organizations: demoOrganizations, activeOrganizationId: DEMO_IDS.organization });
  changing = false;
}

function restoreWorkspace() {
  const saved = snapshot;
  snapshot = null;
  try { sessionStorage.removeItem(snapshotKey); } catch { /* Optional tab storage. */ }
  if (!saved) return;
  changing = true;
  // Never resurrect an old account after logout or an account switch.
  if (useAuthStore.getState().user?.id === saved.ownerId) {
    useAuthStore.setState({ user: saved.user, organizations: saved.organizations, activeOrganizationId: saved.activeOrganizationId });
  }
  usePreferencesStore.setState(saved.preferences);
  useThemeStore.getState().set(saved.theme);
  useVisibilityStore.getState().set(saved.visibility);
  document.documentElement.style.fontSize = saved.fontSize;
  for (const [key, value] of saved.storage) {
    if (key === "active_org_id" && useAuthStore.getState().user?.id !== saved.ownerId) continue;
    if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value);
  }
  changing = false;
}

export function enterDemoWorkspace(userId: string) {
  if (useAuthStore.getState().user?.id !== userId) throw new Error("The signed-in account changed. Reopen the tour.");
  useDemoWorkspaceStore.getState().enter(userId);
  projectDemoUser();
}
export function exitDemoWorkspace() { useDemoWorkspaceStore.getState().exit(); }
export function resetDemoWorkspace() {
  clearQueryBoundary();
  useDemoWorkspaceStore.getState().reset();
}

// Covers normal requests, multipart upload, downloads and assistant streams.
installDemoFetchGuard(() => useAuthStore.getState().user?.id ?? null);
useDemoWorkspaceStore.subscribe((next, previous) => {
  if (next.active !== previous.active || next.ownerId !== previous.ownerId) {
    clearQueryBoundary();
    if (next.active) projectDemoUser(); else restoreWorkspace();
  }
});
useAuthStore.subscribe((next, previous) => {
  if (changing || !useDemoWorkspaceStore.getState().active) return;
  if (!next.user && !next.isLoading) exitDemoWorkspace();
  else if (next.user !== previous.user || next.organizations !== previous.organizations || next.activeOrganizationId !== previous.activeOrganizationId) projectDemoUser();
});
projectDemoUser();
